/**
 * USD Billing client (F-174 per-unit metering / F-175 plan changes) +
 * T-134-001 prorated mid-cycle upgrade math.
 *
 * Consumes the stable room of /api/usd-billing (src/routes/usd-billing.js,
 * mounted before the landlord catch-all). Every call unwraps the canonical
 * { success, data } envelope and returns `data` (or {} on a missing body).
 * All route paths below are proven against the backend:
 *   GET  /meter                     usd-billing.js:104  live metered position
 *   POST /meter/sync                usd-billing.js:134  reconcile the meter
 *   GET  /estimate                  usd-billing.js:152  base + overage invoice
 *   GET  /plan                      usd-billing.js:234  current plan + open change
 *   GET  /plan/compare              usd-billing.js:266  US-135 comparison + ROI
 *   POST /plan/upgrade              usd-billing.js:299  immediate, prorated (US-134)
 *
 * The proration helpers mirror lib/billing-plan.js remainingCycleFraction (:92)
 * and calculateUpgradeProration (:122) so the upgrade dialog can preview the
 * mid-cycle charge without a round trip. The server value stays authoritative.
 */

import { landlordApi } from './api';

// ── tier ordering (mirrors BE TIER_RANK in lib/billing-plan.js) ──────────────
export const TIER_RANK_ORDER = ['freemium', 'essentials', 'professional', 'enterprise'] as const;
export type UsdTierId = (typeof TIER_RANK_ORDER)[number];

// ── response shapes ───────────────────────────────────────────────────────────
export interface UsdCycle {
  cycleKey: string;
  cycleStart: string;
  cycleEnd: string;
  days: number;
}

export interface UsdMeter {
  tenantId?: string;
  activeUnits: number;
  cycleBaseline?: number | null;
  unitChanges: number;
  meteredProperties?: number;
  unitsByProperty?: { propertyId: string; units: number }[];
  cycle?: UsdCycle | null;
  updatedAt?: string | null;
}

export interface UsdTierPricing {
  tierId: string;
  name?: string | null;
  baseFee: number;
  includedUnits: number;
  perUnitRate: number;
  active?: boolean;
  productPath?: string | null;
  overageProductPath?: string | null;
  isCurrent?: boolean;
  isTarget?: boolean;
}

export interface UsdEstimateLine {
  kind: string;
  description?: string;
  units?: number;
  unitRate?: number;
  amount: number;
}

export interface UsdEstimate {
  tenantId?: string;
  tier?: {
    id: string;
    name?: string | null;
    active?: boolean;
    baseFee: number;
    includedUnits: number;
    perUnitRate: number;
  } | null;
  activeUnits?: number;
  hasOverage?: boolean;
  invoice?: {
    cycle?: UsdCycle | null;
    currency?: string;
    lineItems?: UsdEstimateLine[];
    subtotal?: number;
    overage?: {
      overageUnits: number;
      amount: number;
      fullCycleAmount: number;
      billable: boolean;
      prorated?: boolean;
    };
  } | null;
  meter?: UsdMeter | null;
}

export interface UsdPlan {
  tier: UsdTierPricing & { isCurrent: true };
  subscriptionStatus?: string | null;
  pendingChange?: { status: string; toTier?: string; effectiveAt?: string; [k: string]: unknown } | null;
  payment?: unknown | null;
}

export interface UsdProrationLine {
  kind: string;
  description?: string;
  amount: number;
}

export interface UsdProration {
  ok: boolean;
  kind?: string;
  fromTier?: string;
  toTier?: string;
  currency?: string;
  cycle?: UsdCycle | null;
  asOf?: string;
  effective?: string;
  remainingFraction?: number;
  remainingDays?: number;
  activeUnits?: number;
  components?: {
    base: { fromRate: number; toRate: number; credit: number; charge: number };
    overage: {
      overageUnitsFrom: number;
      overageUnitsTo: number;
      fromRate: number;
      toRate: number;
      credit: number;
      charge: number;
    };
  };
  credit?: number;
  charge?: number;
  dueNow?: number;
  lineItems?: UsdProrationLine[];
  nextRenewal?: { at?: string; tier?: string; amount?: number; note?: string };
  code?: string;
  message?: string;
}

export interface UsdComparison {
  ok?: boolean;
  tenantId?: string;
  currentTier: string;
  targetTier: string;
  direction: 'upgrade' | 'downgrade' | 'lateral';
  currency: string;
  tiers: UsdTierPricing[];
  overage?: {
    activeUnits: number;
    includedUnits: number;
    overageUnits: number;
    perUnitRate: number;
    monthlyOverage: number;
    annualOverage: number;
  } | null;
  proration?: UsdProration | null;
  prorationError?: UsdProration | null;
  prorationNote?: string | null;
  roi?: {
    monthlyBaseDelta: number;
    addedIncludedUnits: number;
    proratedChargeToday: number;
    nextRenewalDelta: number;
    annualDelta: number;
    headline: string | null;
  };
  catalog?: Record<string, unknown>[];
}

export interface UpgradeResult {
  fromTier?: string;
  toTier?: string;
  effective?: string;
  proration?: UsdProration | null;
  invoice?: { currency?: string; dueNow?: number; lineItems?: UsdProrationLine[]; subtotal?: number };
  nextRenewal?: { at?: string; tier?: string; amount?: number; note?: string };
  fastspring?: { attempted?: boolean; ok?: boolean; error?: string; reason?: string };
}

// ── envelope unwrap ───────────────────────────────────────────────────────────
async function unwrap<T>(promise: Promise<{ data?: unknown }>): Promise<T> {
  const res = await promise;
  const body = res?.data;
  const payload = (body && typeof body === 'object' && 'data' in (body as object) ? (body as { data: T }).data : body) as T;
  return (payload ?? {}) as T;
}

// ── US-134/135 API surface ────────────────────────────────────────────────────
export const usdBillingApi = {
  /** GET /api/usd-billing/meter — live metered position (active units, cycle). */
  getMeter: (): Promise<UsdMeter> => unwrap(landlordApi.get('api/usd-billing/meter')),

  /** POST /api/usd-billing/meter/sync — reconcile the meter with the property list. */
  syncMeter: (): Promise<Record<string, unknown>> =>
    unwrap<Record<string, unknown>>(landlordApi.post('api/usd-billing/meter/sync')),

  /** GET /api/usd-billing/estimate — base + overage monthly bill preview. */
  getEstimate: (): Promise<UsdEstimate> => unwrap(landlordApi.get('api/usd-billing/estimate')),

  /** GET /api/usd-billing/plan — current tier + subscription status. */
  getPlan: (): Promise<UsdPlan> => unwrap(landlordApi.get('api/usd-billing/plan')),

  /** GET /api/usd-billing/plan/compare?target=&units= — US-135 comparison, ROI, proration. */
  getPlanCompare: (target: string, units?: number | null): Promise<UsdComparison> => {
    const qs = new URLSearchParams();
    qs.append('target', target);
    if (units !== undefined && units !== null && Number.isFinite(units)) qs.append('units', String(units));
    return unwrap(landlordApi.get(`api/usd-billing/plan/compare?${qs.toString()}`));
  },

  /** POST /api/usd-billing/plan/upgrade { targetTier } — immediate prorated upgrade (US-134 AC2/AC3). */
  upgradePlan: (targetTier: string): Promise<UpgradeResult> =>
    unwrap(landlordApi.post('api/usd-billing/plan/upgrade', { targetTier })),
};

// ── T-134-001 prorated mid-cycle upgrade math (mirrors lib/billing-plan.js) ───
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Fraction of the current billing cycle that remains (0..1), per BE remainingCycleFraction. */
export function cycleFractionRemaining(remainingDays: number, cycleDays: number): number {
  const days = Math.max(0, Math.min(Number(remainingDays) || 0, Number(cycleDays) || 0));
  const total = Math.max(1, Number(cycleDays) || 1);
  return Math.round((days / total) * 1e6) / 1e6;
}

export interface ProrationPreviewInput {
  fromBaseFee: number;
  toBaseFee: number;
  fromIncludedUnits: number;
  toIncludedUnits: number;
  fromPerUnitRate: number;
  toPerUnitRate: number;
  activeUnits: number;
  remainingDays: number;
  cycleDays: number;
}

export interface ProrationPreview {
  fraction: number;
  remainingDays: number;
  baseCredit: number;
  baseCharge: number;
  overageFromUnits: number;
  overageToUnits: number;
  credit: number;
  charge: number;
  dueNow: number;
}

/**
 * Instant prorated mid-cycle upgrade math for the target tier. Mirrors
 * calculateUpgradeProration (billing-plan.js:122): credit for the unused
 * remainder of the current tier, charge for the target tier over the same
 * fraction, plus the per-unit delta on currently metered overage units.
 */
export function proratedUpgradePreview(input: ProrationPreviewInput): ProrationPreview {
  const fraction = cycleFractionRemaining(input.remainingDays, input.cycleDays);
  const fromBase = Number(input.fromBaseFee) || 0;
  const toBase = Number(input.toBaseFee) || 0;
  const fromRate = Number(input.fromPerUnitRate) || 0;
  const toRate = Number(input.toPerUnitRate) || 0;
  const units = Math.max(0, Number(input.activeUnits) || 0);
  const overageFromUnits = Math.max(0, units - (Number(input.fromIncludedUnits) || 0));
  const overageToUnits = Math.max(0, units - (Number(input.toIncludedUnits) || 0));

  const baseCredit = round2(fromBase * fraction);
  const baseCharge = round2(toBase * fraction);
  const overageCredit = round2(overageFromUnits * fromRate * fraction);
  const overageCharge = round2(overageToUnits * toRate * fraction);
  const credit = round2(baseCredit + overageCredit);
  const charge = round2(baseCharge + overageCharge);
  const dueNow = round2(Math.max(0, charge - credit));

  return { fraction, remainingDays: input.remainingDays, baseCredit, baseCharge, overageFromUnits, overageToUnits, credit, charge, dueNow };
}

// ── T-135-002 overage cost estimator (client mirror of BE invoice math) ───────
export interface TierMonthlyEstimate {
  overageUnits: number;
  overageCost: number;
  total: number;
}

/** A tier's monthly total for a unit count: base + (units - included) * perUnitRate. */
export function estimateTierMonthly(
  baseFee: number,
  includedUnits: number,
  perUnitRate: number,
  units: number,
): TierMonthlyEstimate {
  const base = Number(baseFee) || 0;
  const included = Number(includedUnits) || 0;
  const rate = Number(perUnitRate) || 0;
  const n = Math.max(0, Number(units) || 0);
  const overageUnits = Math.max(0, n - included);
  const overageCost = round2(overageUnits * rate);
  return { overageUnits, overageCost, total: round2(base + overageCost) };
}